import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import { useGridFilter } from 'ag-grid-react';
import { FILTER_STYLES } from '../../AgToolingComplianceGrid/AgGridCompliance.config';
import {
  USER_NAME_COLUMN_FILTER_TYPE,
  isUserNameColumnFilterModel,
  type UserNameColumnFilterModel,
  type UserNameColumnFilterMode,
} from '../userNameColumnFilter';

type ModeOption = 'all' | UserNameColumnFilterMode;

const MODE_OPTIONS: Array<{ value: ModeOption; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'anyUser', label: 'Has user' },
  { value: 'specific', label: 'Specific user' },
];

function getMode(model: UserNameColumnFilterModel | null | undefined): ModeOption {
  if (!model) {
    return 'all';
  }
  return model.mode;
}

function getNameSearch(model: UserNameColumnFilterModel | null | undefined): string {
  return model?.nameSearch ?? '';
}

export const UserNameColumnFilter = ({
  model,
  onModelChange,
}: CustomFilterProps<unknown, unknown, UserNameColumnFilterModel>) => {
  const refMode = useRef<HTMLSelectElement>(null);
  const prevModelRef = useRef<UserNameColumnFilterModel | null | undefined>(model);
  const [mode, setMode] = useState<ModeOption>(() => getMode(model));
  const [nameSearch, setNameSearch] = useState(() => getNameSearch(model));

  useEffect(() => {
    if (model) {
      setMode(getMode(model));
      setNameSearch(getNameSearch(model));
      prevModelRef.current = model;
      return;
    }

    // Grid cleared this filter externally (URL navigation, clear filters, etc.).
    // Reset UI unless the user just picked "Specific user" and has not typed yet.
    if (isUserNameColumnFilterModel(prevModelRef.current)) {
      setMode('all');
      setNameSearch('');
    }
    prevModelRef.current = model;
  }, [model]);

  const doesFilterPass = useCallback(() => true, []);
  const afterGuiAttached = useCallback(() => {
    refMode.current?.focus();
  }, []);
  useGridFilter({ doesFilterPass, afterGuiAttached });

  const publishModel = (nextMode: ModeOption, nextNameSearch: string) => {
    if (nextMode === 'all') {
      onModelChange(null);
      return;
    }
    if (nextMode === 'anyUser') {
      onModelChange({
        filterType: USER_NAME_COLUMN_FILTER_TYPE,
        mode: 'anyUser',
      });
      return;
    }
    const trimmed = nextNameSearch.trim();
    if (!trimmed) {
      onModelChange(null);
      return;
    }
    onModelChange({
      filterType: USER_NAME_COLUMN_FILTER_TYPE,
      mode: 'specific',
      nameSearch: trimmed,
    });
  };

  const handleModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMode = e.target.value as ModeOption;
    setMode(nextMode);
    const nextNameSearch = nextMode === 'specific' ? nameSearch : '';
    if (nextMode !== 'specific') {
      setNameSearch('');
    }
    publishModel(nextMode, nextNameSearch);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextNameSearch = e.target.value;
    setMode('specific');
    setNameSearch(nextNameSearch);
    publishModel('specific', nextNameSearch);
  };

  const handleReset = () => {
    setMode('all');
    setNameSearch('');
    onModelChange(null);
  };

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement | HTMLInputElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.focusBorder;
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement | HTMLInputElement | HTMLButtonElement>) => {
    e.currentTarget.style.border = FILTER_STYLES.defaultBorder;
  };

  return (
    <div style={FILTER_STYLES.container}>
      <div style={FILTER_STYLES.section}>
        <h4 style={FILTER_STYLES.sectionHeading}>User</h4>
        <div style={FILTER_STYLES.fieldGroup}>
          <label style={FILTER_STYLES.label}>Filter by:</label>
          <select
            ref={refMode}
            style={FILTER_STYLES.select}
            value={mode}
            onChange={handleModeChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {mode === 'specific' && (
          <div style={FILTER_STYLES.fieldGroup}>
            <label style={FILTER_STYLES.label}>Name contains:</label>
            <input
              type="text"
              style={FILTER_STYLES.select}
              value={nameSearch}
              onChange={handleNameChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="Search by name"
            />
          </div>
        )}
      </div>
      <div style={FILTER_STYLES.resetButtonContainer}>
        <button type="button" style={FILTER_STYLES.resetButton} onClick={handleReset} onFocus={handleFocus} onBlur={handleBlur}>
          Reset
        </button>
      </div>
    </div>
  );
};

export default UserNameColumnFilter;
